import { ColumnDef, flexRender, getCoreRowModel, Row, useReactTable } from "@tanstack/react-table";
import { useCallback, useMemo, useRef } from "react";
import Loader from "../Loader/Loader";
import "./DataTable.styles.scss";
import useStickyHeader from "./useStickyHeader";

export interface DataTableProps<T extends object> {
    data: T[];
    columns: ColumnDef<T>[];
    onRowClick?: (record: T) => void;
    stickyTopOffset?: number;
    isLoading?: boolean;
    emptyText?: string;
    showBottomLoader?: boolean;
    showOverlayLoader?: boolean;
    wrapperClassName?: string;
}

function DataTable<T extends object>({
    data,
    columns,
    onRowClick,
    stickyTopOffset,
    isLoading = false,
    emptyText = "No data",
    showBottomLoader = false,
    showOverlayLoader = false,
    wrapperClassName = ""
}: Readonly<DataTableProps<T>>): JSX.Element {
    const table = useReactTable({ data, columns, getCoreRowModel: getCoreRowModel() });

    const headerRef = useRef<HTMLTableSectionElement>(null);
    const wrapperRef = useRef<HTMLDivElement>(null);
    const stickyHeaderRef = useRef<HTMLDivElement>(null);

    // Only disable sticky behavior for initial load or empty state
    const shouldDisableSticky = (isLoading && !showBottomLoader && data.length === 0) || (!isLoading && data.length === 0);

    const { isVisible: isStickyVisible, offsetTop } = useStickyHeader({
        headerRef,
        wrapperRef,
        stickyHeaderRef,
        stickyTopOffset,
        disabled: shouldDisableSticky
    });

    const headerRows = useMemo(
        () =>
            table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id} className="Table__header-row">
                    {headerGroup.headers.map((header) => (
                        <th key={header.id} className="Table__header-cell" style={{ width: header.getSize() }}>
                            {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                        </th>
                    ))}
                </tr>
            )),
        [table, columns]
    );

    const handleRowClick = useCallback((row: Row<T>) => onRowClick?.(row.original), [onRowClick]);

    const recordHasAccessFlag = (record: unknown): record is { hasAccess: boolean } =>
        typeof (record as { hasAccess?: boolean } | undefined)?.hasAccess === "boolean";

    const isRowDisabled = (row: Row<T>): boolean => {
        const record = row.original as unknown;
        if (recordHasAccessFlag(record)) {
            return record.hasAccess === false;
        }
        return false;
    };

    const containerClassName = `Table ${wrapperClassName} ${shouldDisableSticky ? 'no-sticky' : ''} ${isLoading ? 'is-loading' : ''} ${data.length === 0 ? 'is-empty' : ''}`;

    return (
        <div className={containerClassName} ref={wrapperRef}>
            <div className="Table__wrapper">
                <div className="Table__content">
                    <table className="Table__table">
                        <thead className={`Table__header ${shouldDisableSticky ? 'no-sticky' : ''}`} ref={headerRef}>
                            {headerRows}
                        </thead>
                        <tbody className="Table__body">
                            {table.getRowModel().rows.map((row) => (
                                <tr
                                    key={row.id}
                                    className={`Table__row ${isRowDisabled(row) ? "Table__row--disabled" : ""}`}
                                    onClick={() => {
                                        if (!isRowDisabled(row)) {
                                            handleRowClick(row);
                                        }
                                    }}
                                >
                                    {row.getVisibleCells().map((cell) => (
                                        <td key={cell.id} className="Table__cell">
                                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    {showBottomLoader && (
                        <div className="Table__bottom-loader">
                            <Loader />
                        </div>
                    )}
                    {!shouldDisableSticky && (
                        <div
                            ref={stickyHeaderRef}
                            className={`Table__sticky-header ${isStickyVisible ? "is-visible" : ""}`}
                            style={{ top: offsetTop }}
                        >
                            <table className="Table__table">
                                <thead>{headerRows}</thead>
                            </table>
                        </div>
                    )}
                </div>
                {showOverlayLoader && (
                    <div className="Table__overlay-loader">
                        <Loader />
                    </div>
                )}
                {data.length === 0 && !isLoading && (
                    <div className="Table__empty">{emptyText}</div>
                )}
            </div>
        </div>
    );
}

export default DataTable;
